/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

import { Auth } from "aws-amplify";

import {
	storeTokens,
	setTokenCookie,
	setRefreshTokenCookie,
	cleanLocalStorage,
} from "./tokenHelper";
import { eraseAllCookies } from "./cookieHelper";
import getStringParams from "./getStringParams";
import { STORAGE_KEYS } from "../const";

const handleAuthUIStateChange = async (authenticated) => {
	if (authenticated) {
		const { redirect_uri, authorization_code, clientState, isDebug } =
			getStringParams();

		const authenticatedUser = await Auth.currentAuthenticatedUser();
		const userAttributes = await Auth.userAttributes(authenticatedUser);

		let identities = [];

		/**
		 * All new accounts by default should be asked for personal information.
		 */
		let contactNeeded = "true";

		for (const attribute of userAttributes) {
			if (attribute.Name === "identities") {
				identities = JSON.parse(attribute.Value);
			}
			if (attribute.Name === "custom:contactNeeded") {
				/**
				 * If user already provided personal information, then he will have "custom:contactNeeded" field
				 * equal to "false".
				 */
				contactNeeded = attribute.Value;
			}
		}

		if (isDebug) {
			contactNeeded = "true";
		}

		if (identities.length > 0 && contactNeeded === "true") {
			const firstIdentity = identities[0];
			const providerName = firstIdentity.providerName;
			const dateCreated = firstIdentity.dateCreated;

			// timestamp 1698048000000 -> Mon Oct 23 2023 08:00:00 GMT+0000
			if (
				(providerName === "Google" && dateCreated >= 1698048000000) ||
				isDebug
			) {
				/**
				 * Delete local storage "debug" flag, so we could continue log in flow,
				 * after filling personal information. In other case, we will also be redirected
				 * to personal information form.
				 */
				localStorage.removeItem(STORAGE_KEYS.debug);
				/**
				 * Drop execution. User should be redirected to Google Register Page.
				 * We emit "newGoogleUser" event, which handled by listener on Landing Page component.
				 */
				const newUserEvent = new Event("newGoogleUser");
				window.dispatchEvent(newUserEvent);
				return;
			}
		}

		/*
		 * get the current user session
		 */
		const authInfo = await Auth.currentSession();

		const idToken = authInfo.idToken.jwtToken;
		const accessToken = authInfo.accessToken.jwtToken;
		const refreshToken = authInfo.refreshToken.token;

		/*
		 * Set the ID and access token cookies for fast SSO
		 */
		if (idToken && accessToken && refreshToken) {
			setTokenCookie("id_token", idToken);
			setTokenCookie("access_token", accessToken);

			/*
			 * Set the refresh token cookie. Refresh token cannot be parsed for an an expiry so use the access token to get an expiry.
			 * Although the refresh token has a different (longer) expiry than the access token, this is for the purpose of fast SSO,
			 * so the refresh token cookie will get set again when the id or access token cookie expires
			 */
			setRefreshTokenCookie(refreshToken, accessToken);
		} else {
			console.error(
				"Inconsistent application state: Tokens missing from current session"
			);
			return;
		}

		if (authorization_code && redirect_uri) {
			/*
			 * PKCE Flow
			 */

			//Store tokens in DynamoDB
			const response = await storeTokens(
				authorization_code,
				idToken,
				accessToken,
				refreshToken
			);

			if (response.status === 200) {
				cleanLocalStorage();
				window.location.replace(
					redirect_uri +
						"/?code=" +
						authorization_code +
						(clientState !== undefined
							? "&state=" + clientState
							: "")
				);
			} else {
				console.error(
					"Could not store tokens. Server response: " + response.data
				);
			}
		} else if (redirect_uri) {
			/*
			 * Implicit Flow
			 */
			cleanLocalStorage();
			window.location.replace(
				redirect_uri +
					"/?id_token=" +
					idToken +
					(clientState !== undefined ? "&state=" + clientState : "")
			);
		} else {
			/*
			 * Sign in directly to broker (not from redirect from client as part of oauth2 flow)
			 */
			//props.history.push('/dashboard');
		}
	} else if (!authenticated) {
		eraseAllCookies();
	}
};

export default handleAuthUIStateChange;
